import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { AddAPhotoOutlined, Close } from '@mui/icons-material'
import { Avatar, Button, Grid, IconButton, Typography } from '@mui/material'
import { Form, Formik, FormikProps } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import BackButton from '../../components/back-button'
import ExitActivationModal from '../../components/exit-activation-modal'
import FormikDropdown from '../../components/formik-dropdown'
import FormikTextField from '../../components/formik-input'
import PhotoUpload from '../../components/photo-upload'
import usePhotoDropzone from '../../components/photo-upload/usePhotoDropzone'
import ScreenContainer from '../../components/screen'
import SensorActivationFooter from '../../components/sensor-activation-footer'
import { useActivationContext } from '../../providers/activation-provider'
import Api from '../../utils/api'
import { convertStringToArray } from '../../utils/helpers'
import useIsMobile from '../../utils/hooks/useIsMobile'
import useSharedStyles from '../../utils/shared.styles'

import useStyles from './index.styles'
import useActivateFolder from './useActivateFolder'

interface FormValues {
  area: string
  building: string
  floor: string
  deviceLocationNotes: string
}

const initialValues: FormValues = {
  area: '',
  building: '',
  floor: '',
  deviceLocationNotes: '',
}

const SensorLocation = () => {
  const sharedClasses = useSharedStyles
  const classes = useStyles

  const { activationLocationState } = useActivationContext()
  const { folderId, nodeTypeName, nodeId, sidewalkId: activationId, meshifyId } = activationLocationState
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const isMobile = useIsMobile()

  const [showExitActivationDialog, setShowExitActivationDialog] = useState<boolean>(false)
  const [deviceLocations, setDeviceLocations] = useState<{ id: string; name: string }[]>([])

  const { activateFolder } = useActivateFolder()

  const {
    photoModalOpen,
    setPhotoModalOpen,
    uploadedPhoto,
    setUploadedPhoto,
    devicePhoto,
    setDevicePhoto,
    getRootProps,
    getInputProps,
    isDragActive,
    openFilePicker,
  } = usePhotoDropzone()

  const schema = Yup.object().shape({
    area: Yup.string().required('This field is required'),
    deviceLocationNotes: Yup.string().required('This field is required'),
  })

  useEffect(() => {
    if (!folderId) navigate('/sensor-folder-assignment', { state: activationLocationState })
  }, [activationLocationState, folderId, navigate])

  useEffect(() => {
    const fetchLocations = async () => {
      if (folderId) {
        const res = await Api.get(`/api/folders/${folderId}/theme`)
        if (res.ok) {
          const apiDeviceLocations = res.body.theme.deviceLocations
          const parsedDeviceLocations = apiDeviceLocations
            ? typeof apiDeviceLocations === 'string'
              ? convertStringToArray(apiDeviceLocations)
              : apiDeviceLocations
            : undefined
          setDeviceLocations((parsedDeviceLocations ?? []).map((deviceLocation: string) => ({ id: deviceLocation, name: deviceLocation })))
        } else {
          enqueueSnackbar('Could not get device locations.', { variant: 'error' })
        }
      }
    }

    fetchLocations()
  }, [enqueueSnackbar, folderId])

  const handleSubmit = async (values: FormValues) => {
    if (nodeId && folderId) {
      // Update the device's location information, move the device to the selected folder, and activate it
      const { area, building, floor, deviceLocationNotes } = values
      const res = await Api.patch(`/api/v2/protect/devices/${nodeId}`, { folderId, area, building, floor, deviceLocationNotes, isActivated: true })
      if (res.ok) {
        await activateFolder(nodeId)
      } else {
        enqueueSnackbar('Could not update device.', { variant: 'error' })
      }
    } else if (!nodeId && folderId) {
      const res = await Api.post(`/api/v2/slim/folders/${folderId}/devices`, {
        activationId,
        area: values.area,
        building: values.building,
        deviceLocationNote: values.deviceLocationNotes,
        floor: values.floor,
        meshifyId,
      })
      if (res.ok) {
        const resActivateDevice = await Api.patch(`/api/v2/protect/devices/${res.body.node.id}`, { isActivated: true })

        // If there is a photo selected, upload it to the newly created device
        if (devicePhoto) {
          const formData = new FormData()
          const blob = await fetch(devicePhoto).then((r) => r.blob())
          formData.append('photo', blob, 'photo.jpg')
          const response = await Api.put(`/api/v2/devices/${res.body.node.id}/photo`, formData)
          if (!response.ok) enqueueSnackbar('Error uploading photo', { variant: 'error' })
        }

        if (resActivateDevice.ok) {
          await activateFolder(res.body.node.id)
        } else {
          enqueueSnackbar('Could not activate device.', { variant: 'error' })
        }
      } else {
        enqueueSnackbar('Could not fulfill device.', { variant: 'error' })
      }
    }
  }

  return (
    <ScreenContainer
      useDarkBackground={true}
      headerProps={{
        title: nodeTypeName === 'nbiot_leak' ? 'Activate' : '',
        showDownloadAppButton: false,
        leftIcon: <BackButton />,
        rightIcon: (
          <IconButton color="inherit" onClick={() => setShowExitActivationDialog(true)} aria-label="Exit activation">
            <Close />
          </IconButton>
        ),
        useDarkBackground: true,
        progress: nodeTypeName === 'nbiot_leak' ? 80 : 93,
      }}
      footerSpacing={148}
    >
      <ExitActivationModal showExitActivationDialog={showExitActivationDialog} setShowExitActivationDialog={setShowExitActivationDialog} />

      <Grid container={true} sx={classes.outerContainer} {...getRootProps()}>
        <form encType="multipart/form-data">
          <input id="device-photo-upload" aria-label="Device photo upload" {...getInputProps()} />

          <Grid container={true} size={12} sx={classes.photoColumn}>
            <Avatar onClick={openFilePicker} src={devicePhoto || undefined} sx={classes.avatar}>
              <AddAPhotoOutlined sx={classes.avatarIcon} />
            </Avatar>

            <Button variant="text" onClick={openFilePicker}>
              <Typography variant="subtitle1" sx={classes.addPhotoLabel}>
                Add Location Photo
              </Typography>
            </Button>

            <Typography variant="body2" sx={[classes.recommendedLabel, classes.recommendedText]}>
              Recommended
            </Typography>
          </Grid>
        </form>

        <PhotoUpload
          deviceId={nodeId}
          open={photoModalOpen}
          onClose={() => {
            setPhotoModalOpen(false)
          }}
          onSave={(newPhoto: string) => {
            setDevicePhoto(newPhoto)
            setUploadedPhoto(null)
            setPhotoModalOpen(false)
          }}
          uploadedPhoto={uploadedPhoto}
          isDragActive={isDragActive}
        />

        <Grid container={true} size={12} sx={classes.formContentColumn}>
          <Typography variant="h6" sx={classes.formHeading}>
            Adding <span style={classes.textEmphasis}>detailed information</span> below will help you easily locate your sensor during alerts.
          </Typography>

          <Grid size={12}>
            <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
              {({ isValid, isSubmitting, values }: FormikProps<FormValues>) => (
                <Form>
                  <Grid container={true} sx={classes.formFieldsContainer}>
                    <FormikDropdown
                      variant="filled"
                      name="area"
                      label="Select device location"
                      required={true}
                      items={deviceLocations}
                      size="medium"
                      sx={sharedClasses.input}
                      autoComplete="off"
                      hideErrors={true}
                    />

                    <Grid sx={classes.notesWrapper}>
                      <FormikTextField
                        variant="filled"
                        name="deviceLocationNotes"
                        label="Device location details"
                        required={true}
                        multiline={true}
                        rows={1}
                        sx={{ ...sharedClasses.input, marginBottom: 0 }}
                        autoComplete="off"
                        hideErrors={true}
                      />
                      <Typography variant="caption" noWrap={true} sx={classes.captionText(isMobile)}>
                        Add details e.g. “cabinet, below the sink”
                      </Typography>
                    </Grid>

                    <Typography variant="subtitle2" sx={classes.optionalLabel}>
                      Optional
                    </Typography>

                    <Grid container={true} size={12} sx={classes.optionalFieldsRow}>
                      <Grid size={8}>
                        <FormikTextField variant="filled" name="building" label="Building, Apt, Suite" sx={sharedClasses.input} autoComplete="off" />
                      </Grid>
                      <Grid size={3.5}>
                        <FormikTextField variant="filled" name="floor" label="Floor" sx={sharedClasses.input} autoComplete="off" />
                      </Grid>
                    </Grid>

                    <SensorActivationFooter
                      primaryButton={
                        <Button
                          type="submit"
                          variant="contained"
                          color="secondary"
                          disableElevation
                          loading={isSubmitting}
                          disabled={!isValid || !values.area}
                          sx={{ paddingX: 4 }}
                        >
                          Next
                        </Button>
                      }
                      sxFooter={classes.sxFooter}
                    />
                  </Grid>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Grid>
    </ScreenContainer>
  )
}

export default SensorLocation
